import { useState } from "react";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { 
  BirthdayFundRequest,
  BirthdayFundBreakdown,
  BirthdayCertificate
} from "@/lib/api/fund";
import { 
  useListCertificateInstances,
  useAssociateCertificateToBreakdown,
  useDisassociateCertificateFromBreakdown,
  CertificateInstance
} from "@/lib/api/birthdayFundCertificates";
import { GenerateLoadingOrError, CommonDestinationTimezone } from "@/lib/utils";

interface AssociateCertificateModalProps {
  request: BirthdayFundRequest | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AssociateCertificateModal({
  request,
  isOpen,
  onClose,
}: AssociateCertificateModalProps) {
  const { toast } = useToast();
  const [selectedCertificateId, setSelectedCertificateId] = useState<string>("");
  const [selectedBreakdownId, setSelectedBreakdownId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const certificateInstancesResult = useListCertificateInstances();
  const associateCertificate = useAssociateCertificateToBreakdown(request?.id || "");
  const disassociateCertificate = useDisassociateCertificateFromBreakdown(request?.id || "");

  const loadingOrErrorUi = GenerateLoadingOrError([certificateInstancesResult]);

  // Filter certificates that are not associated to anything
  const unassociatedCertificates = (certificateInstancesResult?.data?.data?.instances || []).filter(
    (cert: CertificateInstance) => !cert.birthday_fund_request_id && !cert.birthday_fund_breakdown_id
  );

  // Get breakdowns for this request
  const breakdowns: BirthdayFundBreakdown[] = (() => {
    if (!request?.breakdowns) return [];
    // Handle both array and single object formats
    if (Array.isArray(request.breakdowns)) {
      return request.breakdowns;
    } else if (request.breakdowns.id) {
      return [request.breakdowns];
    }
    return [];
  })();

  // Filter to only projected breakdowns that don't have a certificate
  const availableBreakdowns = breakdowns.filter(
    (breakdown: BirthdayFundBreakdown) => 
      breakdown.status === 'projected' &&
      (!breakdown.certificates || breakdown.certificates.length === 0)
  );

  // Get breakdowns with certificates for display
  const breakdownsWithCertificates = breakdowns.filter(
    (breakdown: BirthdayFundBreakdown) => 
      breakdown.certificates && breakdown.certificates.length > 0
  );

  // Sort certificates: matching location first, then by creation date (newest first)
  const requestLocation = request?.survey?.data?.party_location?.toLowerCase() || '';
  const sortedCertificates = [...unassociatedCertificates].sort((a, b) => {
    const aLocationMatch = a.location_name.toLowerCase() === requestLocation;
    const bLocationMatch = b.location_name.toLowerCase() === requestLocation;
    
    // If one matches and the other doesn't, matching one comes first
    if (aLocationMatch && !bLocationMatch) return -1;
    if (!aLocationMatch && bLocationMatch) return 1;
    
    // If both match or both don't match, sort by creation date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const certificateOptions = sortedCertificates.map((cert: CertificateInstance) => ({
    value: cert.id,
    label: `${cert.certificate_id} - ${cert.location_name} - ${cert.status} - Created ${moment.tz(cert.created_at, CommonDestinationTimezone).format("MM/DD/YYYY")}`,
  }));

  const breakdownOptions = availableBreakdowns.map((breakdown: BirthdayFundBreakdown) => ({
    value: breakdown.id,
    label: `${breakdown.name} ($${breakdown.amount})`,
  }));

  const handleSubmit = async () => {
    if (!selectedCertificateId || !selectedBreakdownId) {
      toast({
        title: "Missing selection",
        description: "Please select both a certificate and a breakdown",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await associateCertificate({
        certificate_instance_id: selectedCertificateId,
        breakdown_id: selectedBreakdownId,
      });

      if (result.success) {
        toast({
          title: "Success",
          description: "Certificate associated to breakdown",
        });
        setSelectedCertificateId("");
        setSelectedBreakdownId("");
      } else {
        throw new Error(result.message || "Failed to associate certificate");
      }
    } catch (error: any) {
      console.error("Failed to associate certificate", error);
      toast({
        title: "Error",
        description: error.message || "Failed to associate certificate",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedCertificateId("");
    setSelectedBreakdownId("");
    onClose();
  };

  const handleDisassociate = async (cert: BirthdayCertificate, breakdownId: string) => {
    try {
      const result = await disassociateCertificate({
        certificate_instance_id: cert.id,
        breakdown_id: breakdownId,
      });

      if (result.success) {
        toast({
          title: "Certificate Disassociated",
          description: "The certificate has been removed from the breakdown",
        });
      } else {
        throw new Error(result.message || "Failed to disassociate certificate");
      }
    } catch (error: any) {
      console.error("Failed to disassociate certificate", error);
      toast({
        title: "Error",
        description: error.message || "Failed to disassociate certificate",
        variant: "destructive",
      });
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Associate Certificate to Breakdown</DialogTitle>
          <DialogDescription>
            Link an unassociated certificate to a projected breakdown
          </DialogDescription>
        </DialogHeader>

        {loadingOrErrorUi ? (
          loadingOrErrorUi
        ) : request.expired_at ? (
          <div className="rounded-lg border bg-yellow-50 border-yellow-200 p-4 text-yellow-800 text-sm">
            Expired requests cannot have certificates associated.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Request Info - Detailed */}
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Birthday Fund ID:</span>
                  <span className="ml-2 font-semibold">#{request.birthday_fund_number}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Nominator:</span>
                  <span className="ml-2 font-semibold">
                    {request.survey.data.nominator_first_name} {request.survey.data.nominator_last_name}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="ml-2">{request.nominated_organization}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <span className="ml-2">{request.survey.data.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="ml-2">{request.survey.data.phone}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Child Birthday:</span>
                  <span className="ml-2">
                    {request.survey.data.birthday_month_of_child} {request.survey.data.birthday_day_of_child}
                    {request.survey.data.child_age && ` (Age: ${request.survey.data.child_age})`}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Party Location:</span>
                  <span className="ml-2 font-medium">{request.survey.data.party_location || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Projected Breakdowns with Certificate Status */}
            {(() => {
              const projectedBreakdowns = breakdowns.filter((b: BirthdayFundBreakdown) => b.status === 'projected');
              return projectedBreakdowns.length > 0 && (
                <div className="rounded-lg border bg-muted/50 p-3">
                  <div className="text-sm font-medium mb-2">Projected Breakdowns</div>
                  <div className="space-y-1">
                    {/* Table header */}
                    <div className="grid grid-cols-[2fr_2fr] gap-2 text-xs font-medium text-muted-foreground border-b pb-1">
                      <div>Item</div>
                      <div className="text-right">Certificate</div>
                    </div>
                    {/* Table rows */}
                    {projectedBreakdowns.map((breakdown: BirthdayFundBreakdown) => {
                      const cert = breakdown.certificates && breakdown.certificates.length > 0 ? breakdown.certificates[0] : null;
                      return (
                        <div key={breakdown.id} className="grid grid-cols-[2fr_2fr] gap-2 text-sm py-1 border-b last:border-0">
                          <div className="font-medium">{breakdown.name}</div>
                          <div className="text-right text-xs text-muted-foreground flex items-center justify-end gap-2">
                            {cert ? (
                              <>
                                <span>{cert.certificate_id} ({cert.status})</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => handleDisassociate(cert, breakdown.id)}
                                >
                                  Disassociate
                                </Button>
                              </>
                            ) : (
                              <span className="italic">No certificate</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
              {/* Certificate Selection */}
              <div className="space-y-1.5">
                <div className="text-sm font-medium">Certificate</div>
                {unassociatedCertificates.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No unassociated certificates available
                  </p>
                ) : (
                  <Combobox
                    options={certificateOptions}
                    value={selectedCertificateId}
                    onValueChange={setSelectedCertificateId}
                    placeholder="Select a certificate..."
                    isModal
                    dwimOnChange
                    popoverClassName="w-[600px]"
                  />
                )}
              </div>

              {/* Breakdown Selection */}
              <div className="space-y-1.5">
                <div className="text-sm font-medium">Breakdown</div>
                {availableBreakdowns.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No breakdowns remaining
                  </p>
                ) : (
                  <Combobox
                    options={breakdownOptions}
                    value={selectedBreakdownId}
                    onValueChange={setSelectedBreakdownId}
                    placeholder="Select a breakdown..."
                    isModal
                    dwimOnChange
                    popoverClassName="w-[400px]"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !selectedCertificateId || !selectedBreakdownId}
          >
            {isSubmitting ? "Associating..." : "Associate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
